import { useMemo } from 'react';

import { useListInfiniteLocations } from '@givebacks/api/offers';
import { useUrlSearchParams } from 'core/Hooks';

import { rewardDiningSortValidation } from '../rewardFilterValidation';
import { useMapLocation } from './mapLocation.provider';

const DEFAULT_MILES_RADIUS = 10;
const INITIAL_DATA_LIMIT = 100;

/**
 * Shared hook for fetching in-store merchant locations. Reads filter/sort options from URL params.
 * All components using this hook share the same data via React Query's cache deduplication.
 */
const useInStoreLocations = () => {
  const { lat, lng } = useMapLocation();

  const { order } = useUrlSearchParams(['order']);

  const searchOrder = rewardDiningSortValidation.parse(order);

  const queryOptions = useMemo(() => {
    const descendingSortFields = ['promotional_amount', 'created_at'];

    const search = {
      searches: {
        // API isn't semantic, it actually searches for lat,lng instead of lon,lat
        lonlat: { value: `${lat},${lng},${DEFAULT_MILES_RADIUS}` },
      },
    };

    if (searchOrder) {
      Object.assign(search, {
        order: {
          field: searchOrder,
          direction: descendingSortFields.includes(searchOrder) ? 'desc' : 'asc',
        },
      });
    }

    // Add promotional_type filter when sorting by promotional_amount
    if (searchOrder === 'promotional_amount') {
      Object.assign(search.searches ?? {}, { promotional_type: { value: 'percentage' } });
    }

    return search;
  }, [lat, lng, searchOrder]);

  const query = useListInfiniteLocations({
    queryOptions: {
      ...queryOptions,
      join: 'AND',
    },
    initialLimit: INITIAL_DATA_LIMIT,
  });

  const flattenedData = useMemo(
    () => query.data?.pages.flatMap((page) => page.locations) ?? [],
    [query.data?.pages]
  );

  return {
    ...query,
    locations: flattenedData,
  };
};

export { useInStoreLocations };
